import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { DiscordIcon, Link, Skeleton, Tooltip } from '@blade/ui-kit'
import { unsafeUrlDialogEvent } from '@root/entities/deeplinking/unsafe-url-dialog/atoms'
import * as KnownSocialPlatform from '@root/entities/socials/model/known-social-platform'
import * as KnownSocialPlatformResource from '@root/entities/socials/model/known-social-platform-resource'
import * as Socials from '@root/entities/socials/model/socials'
import {
  normalizeDiscordHref,
  normalizeXHref,
} from '@root/entities/socials/model/socials-normalize'
import { SocialEmbed } from '@root/entities/socials/ui/social-embed'
import { ExploreDevTwitterTooltip } from '@root/features/explore/ui/explore-dev-tooltip/explore-dev-twitter-tooltip'
import { TelegramLinks } from '@root/features/premium/currency-information/ui/telegram-links'
import { isTauriAtom } from '@root/shared/core'
import { truncateEnd } from '@root/shared/core/StringUtil'
import { useTranslation } from '@root/shared/localization'
import { TooltipCard, XSocialIcon } from '@root/shared/ui-kit'

import { WebsiteUrlSocialIcon } from './currency-information-social-icon'

import type { UnsafeUrlProvider } from '@root/entities/deeplinking/unsafe-url-dialog/model'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  socials: Socials.Socials
  size?: number
  variant?: 'default' | 'detailed'
} & ClassNameProps

const getEmbedBorderRadius = (
  resource: KnownSocialPlatformResource.KnownSocialPlatformResource,
) =>
  KnownSocialPlatformResource.matchEmbedType(resource, {
    onIframe: (socialPlatformResource) =>
      KnownSocialPlatformResource.matchIframeEmbedable(socialPlatformResource, {
        telegram: () => 15,
        youtube: () => 6,
        tiktok: () => 6,
        instagram: () => 6,
        facebook: (facebook) => (facebook.type === 'page' ? 16 : 0),
        twitch: () => 6,
        reddit: () => 6,
        substack: () => 12,
      }),
    onX: () => 10,
    onWiki: () => 6,
    onGithub: () => 6,
    onImage: () => 6,
    onBluesky: () => 32,
    onFacebook: () => 0,
    onPinterest: (p) =>
      KnownSocialPlatformResource.matchPinterestMediaType(p.type, {
        onPin: () => 36 /* for large pin */,
        onUser: () => 16,
        onBoard: () => 16,
      }),
    onTiktok: () => 6,
  })

const getEmbedOpenSide = (
  resource: KnownSocialPlatformResource.KnownSocialPlatformResource,
) =>
  KnownSocialPlatformResource.matchEmbedType(resource, {
    onIframe: KnownSocialPlatformResource.matchIframeEmbedable({
      telegram: () => 'right' as const,
      youtube: (yt) =>
        KnownSocialPlatformResource.matchYoutubeMediaType(yt.type, {
          onVideo: () => 'top' as const,
          onShorts: () => 'right' as const,
          onLive: () => 'top' as const,
          onChannel: () => 'right' as const,
          onPlaylist: () => 'right' as const,
        }),
      tiktok: () => 'right' as const,
      instagram: () => 'right' as const,
      facebook: (resource) =>
        KnownSocialPlatformResource.matchFacebookMediaType(resource.type, {
          onReel: () => 'right' as const,
          onVideo: () => 'top' as const,
          onPost: () => 'top' as const,
          onPage: () => 'right' as const,
        }),
      twitch: () => 'top' as const,
      reddit: () => 'right' as const,
      substack: () => 'right' as const,
    }),
    onX: () => 'right' as const,
    onWiki: () => 'top' as const,
    onGithub: () => 'top' as const,
    onImage: () => 'top' as const,
    onBluesky: () => 'right' as const,
    onFacebook: () => 'right' as const,
    onPinterest: () => 'right' as const,
    onTiktok: () => 'right' as const,
  })

export function CurrencyInformationSocial({
  socials,
  className,
  size = 14,
  variant = 'default',
}: Props) {
  const { t } = useTranslation()
  const [isTauri] = useAtom(isTauriAtom)

  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)

  const { x, telegram, discord, knownPlatform } = socials

  const noSocials = !x && !knownPlatform && !telegram && !discord

  const websiteLabel = useMemo(() => {
    if (variant !== 'detailed' || !knownPlatform) {
      return undefined
    }
    if (
      knownPlatform instanceof KnownSocialPlatform.WebsiteKnownSocialPlatform
    ) {
      return t('common.website')
    }
    return knownPlatform.label
  }, [t, variant, knownPlatform])

  if (noSocials) {
    return undefined
  }

  const discordHref = discord ? normalizeDiscordHref(discord) : undefined

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    href: string | URL,
    name: UnsafeUrlProvider,
  ) => {
    e.stopPropagation()
    e.preventDefault()

    openUnsafeUrlDialog([href, name])
  }

  const renderTwitterInfo = () => {
    if (socials.twitterInfo) {
      const twitterInfoUserHref = normalizeXHref(
        socials.twitterInfo.user.username,
      )
      return (
        <ExploreDevTwitterTooltipCard
          clickArea={
            <ExploreDevTwitterTooltip twitterInfo={socials.twitterInfo.user} />
          }
        >
          <SocialLink
            onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
              handleClick(e, twitterInfoUserHref, 'x')
            }
            target="_blank"
            href={twitterInfoUserHref}
          >
            <XSocialIcon size={size} color="var(--text-11)" />
          </SocialLink>
        </ExploreDevTwitterTooltipCard>
      )
    }

    if (x) {
      const xHref = normalizeXHref(Socials.getXString(x))
      const socialLink = (
        <SocialLink
          data-variant={variant}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            handleClick(e, xHref, 'x')
          }
          target="_blank"
          href={xHref}
        >
          <XSocialIcon size={size} color="var(--text-11)" />{' '}
          {variant === 'detailed' ? 'X' : undefined}
        </SocialLink>
      )

      if (x instanceof KnownSocialPlatform.XKnownSocialPlatform) {
        const xResource = KnownSocialPlatform.resource(x)
        if (xResource) {
          return (
            <EmbedTooltip
              side="left"
              $borderRadius={getEmbedBorderRadius(xResource)}
              clickArea={<SocialEmbed resource={xResource} />}
            >
              {socialLink}
            </EmbedTooltip>
          )
        }
      }

      return (
        <SocialTooltip content={truncateEnd(xHref, { maxLength: 150 })}>
          {socialLink}
        </SocialTooltip>
      )
    }
  }

  const renderWebsiteInfo = () => {
    if (!knownPlatform) {
      return undefined
    }
    const socialLink = (
      <SocialLink
        data-variant={variant}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
          handleClick(e, knownPlatform.url, knownPlatform._tag)
        }
        target="_blank"
        href={knownPlatform.url}
      >
        <WebsiteUrlSocialIcon knownPlatform={knownPlatform} />
        {websiteLabel}
      </SocialLink>
    )

    const resource = KnownSocialPlatform.resource(knownPlatform)
    if (!resource) {
      return (
        <SocialTooltip
          content={<SocialTooltipContent platform={knownPlatform} />}
        >
          {socialLink}
        </SocialTooltip>
      )
    }

    if (
      isTauri &&
      !KnownSocialPlatformResource.hasToShowEmbedInTauri(resource)
    ) {
      return (
        <SocialTooltip
          content={<SocialTooltipContent platform={knownPlatform} />}
        >
          {socialLink}
        </SocialTooltip>
      )
    }

    return (
      <EmbedTooltip
        side={getEmbedOpenSide(resource)}
        $borderRadius={getEmbedBorderRadius(resource)}
        clickArea={<SocialEmbed resource={resource} />}
      >
        {socialLink}
      </EmbedTooltip>
    )
  }

  return (
    <Root className={className} data-variant={variant}>
      {renderTwitterInfo()}
      {renderWebsiteInfo()}
      {telegram ? (
        <TelegramLinks size={size} socials={socials} variant={variant} />
      ) : undefined}
      {discord && discordHref ? (
        <SocialTooltip content={truncateEnd(discord, { maxLength: 150 })}>
          <SocialLink
            data-variant={variant}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
              handleClick(e, discordHref, 'discord')
            }
            target="_blank"
            href={discordHref}
          >
            <DiscordIcon size={size} />{' '}
            {variant === 'detailed' ? t('common.discord') : undefined}
          </SocialLink>
        </SocialTooltip>
      ) : undefined}
    </Root>
  )
}

const Root = styled.span`
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 3px;

  svg {
    cursor: pointer;
  }
  a {
    color: var(--text-11);
  }

  &[data-variant='detailed'] {
    height: auto;
  }
`

CurrencyInformationSocial.Skeleton = (props: { size?: number }) => {
  const size = props.size ?? 14
  return (
    <Root>
      <Skeleton height={size} width={size} />
      <Skeleton height={size} width={size} />
      <Skeleton height={size} width={size} />
    </Root>
  )
}

const SocialTooltip = styled(Tooltip)`
  max-width: unset;
  pointer-events: none;
`

const SocialTooltipContent = ({
  platform,
}: {
  platform: KnownSocialPlatform.KnownSocialPlatform
}) => {
  const domain = KnownSocialPlatform.domain(platform)

  const isWebsite =
    platform instanceof KnownSocialPlatform.WebsiteKnownSocialPlatform
  return (
    <SocialTooltipContentRoot>
      {domain && isWebsite ? (
        <SocialTooltipContentTitle>
          <WebsiteFavicon url={platform.url} size={16} />
          <span>{domain}</span>
        </SocialTooltipContentTitle>
      ) : undefined}
      <SocialTooltipContentLink $isWebsite={isWebsite}>
        {truncateEnd(platform.url, { maxLength: 150 })}
      </SocialTooltipContentLink>
    </SocialTooltipContentRoot>
  )
}

const SocialTooltipContentRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`
const SocialTooltipContentTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`
const SocialTooltipContentLink = styled.div<{ $isWebsite: boolean }>`
  ${({ $isWebsite }) =>
    $isWebsite &&
    css`
      color: var(--text-11);
    `}
`

const SocialLink = styled(Link)`
  &[data-variant='detailed'] {
    border-radius: 6px;
    background: rgba(var(--background-1-rgb), 0.5);
    backdrop-filter: blur(3px);
    padding: 2px 6px;

    display: grid;
    grid-auto-flow: column;
    gap: 4px;
    align-items: center;
    justify-content: center;

    font-size: 10px;

    transition: background 0.2s ease-in-out;

    &:hover {
      background: rgba(var(--background-1-rgb), 0.7);
    }
  }
`

const EmbedTooltip = styled(TooltipCard)<{ $borderRadius: number }>`
  --tooltip-width: auto;
  --tooltip-card-body-border-color: transparent;
  --tooltip-card-body-background: transparent;
  --tooltip-border-radius: ${({ $borderRadius }) => $borderRadius}px;
`
const ExploreDevTwitterTooltipCard = styled(TooltipCard)`
  --tooltip-width: 325px;
`

const WebsiteFavicon = ({ url, size }: { url: string; size: number }) => {
  const faviconUrl = new URL(
    `https://www.google.com/s2/favicons?domain=${url}&sz=${size * 2}`,
  )
  return <WebsiteFaviconRoot url={faviconUrl.toString()} size={size} />
}

const WebsiteFaviconRoot = styled.div<{ url: string; size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 50%;
  background: url(${({ url }) => url}) no-repeat center center;
  background-size: cover;
`
